/**
 * Setting rows: a label on the left, the value against the right margin between ◄ ► while
 * selected. Volumes are a bar of cells and a figure.
 */

import { textWidth } from '../../render/font';
import { GREEN, LIME, YELLOW } from '../../render/palette';
import type { Screen } from '../../render/screen';
import { nudge } from './chrome';
import { ACCENT, blink, FAINT, INK, MUTED, SELECTED } from './theme';

export type RowValue =
  | { readonly kind: 'choice'; readonly text: string }
  | { readonly kind: 'level'; readonly value: number; readonly max: number }
  /** Opens another screen. */
  | { readonly kind: 'link' };

export interface RowColumns {
  /** Left edge of the label. */
  readonly label: number;
  /** Right edge of the value; the ► sits just beyond it. */
  readonly right: number;
}

const CELL_W = 6;
const PITCH = CELL_W + 1;
/** Room for a level's figure (two digits) and the gap before it. */
const FIGURE_W = 20;

/** Colour of volume cell `i` of `max` (quiet green to loud yellow). Pure. */
export function levelColour(i: number, max: number): number {
  const f = max <= 1 ? 1 : i / (max - 1);
  return f < 0.45 ? GREEN : f < 0.8 ? LIME : YELLOW;
}

/** Left edge of what a value draws: its text, its bar, or its arrow. Pure. */
export function valueLeft(value: RowValue, cols: RowColumns): number {
  if (value.kind === 'choice') return cols.right - textWidth(value.text);
  if (value.kind === 'level') return cols.right - FIGURE_W - value.max * PITCH + 1;
  return cols.right - 8;
}

/**
 * What a press at `x` on a row means: a level set straight from its bar, a step back from the ◄,
 * otherwise a step on. Pure.
 */
export function rowPress(value: RowValue, cols: RowColumns, x: number): { step: number } | { set: number } {
  const left = valueLeft(value, cols);
  if (value.kind === 'level' && x >= left && x < left + value.max * PITCH) {
    return { set: Math.min(value.max, Math.floor((x - left) / PITCH) + 1) };
  }
  return { step: x >= left - 14 && x < left ? -1 : 1 };
}

/** Draw one setting row with its text top at `y`. */
export function drawOptionRow(
  screen: Screen, y: number, label: string, value: RowValue, selected: boolean, ms: number, cols: RowColumns,
): void {
  screen.text(label, cols.label, y, selected ? SELECTED : INK);
  const left = valueLeft(value, cols);
  const arrows = blink(ms, 900) ? SELECTED : MUTED;
  if (value.kind === 'link') {
    screen.text('►', left + (selected ? nudge(ms) - 2 : 0), y, selected ? SELECTED : MUTED);
    return;
  }
  if (value.kind === 'choice') screen.text(value.text, left, y, selected ? SELECTED : ACCENT);
  else {
    for (let i = 0; i < value.max; i++) {
      const x = left + i * PITCH;
      if (i < value.value) screen.fill(x, y + 1, CELL_W, 6, levelColour(i, value.max));
      else screen.box(x, y + 1, CELL_W, 6, FAINT);
    }
    screen.text(String(value.value), cols.right, y, selected ? SELECTED : ACCENT, 'right');
  }
  if (selected) {
    screen.text('◄', left - 10, y, arrows);
    screen.text('►', cols.right, y, arrows);
  }
}
